import { useRef, useState } from 'react';

/* ---- helpers ---- */
function digitsOnly(s) {
  return s.replace(/\D/g, '');
}

function normalizeDigits(raw) {
  let d = digitsOnly(raw);
  if (!d) return '';
  // 8-xxx... → 7-xxx... (привычка набирать с 8)
  if (d[0] === '8') d = '7' + d.slice(1);
  // если вдруг нет лидирующей 7 — добавляем
  if (d[0] !== '7') d = '7' + d;
  return d.slice(0, 11); // макс 11 цифр
}

function formatPhone(digits) {
  // digits: нормализованы, начинаются с 7, макс 11
  if (!digits) return '';
  const d = digits;
  if (d.length <= 1)  return '+7';
  if (d.length <= 4)  return `+7 (${d.slice(1)}`;
  if (d.length <= 7)  return `+7 (${d.slice(1, 4)}) ${d.slice(4)}`;
  if (d.length <= 9)  return `+7 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  return                      `+7 (${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7, 9)}-${d.slice(9, 11)}`;
}

// Сколько символов в отформатированной строке после N цифр
function cursorForDigits(formatted, digitCount) {
  let found = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      found++;
      if (found === digitCount) return i + 1;
    }
  }
  return formatted.length;
}

// Валидация казахстанского номера: 11 цифр, +7 7XX...
export function isValidKzPhone(value) {
  const d = digitsOnly(value);
  return d.length === 11 && d[0] === '7' && d[1] === '7';
}

export function rawPhone(value) {
  // Возвращает +7XXXXXXXXXX для отправки на бэк
  const d = normalizeDigits(value);
  return d.length === 11 ? `+${d}` : value;
}

/* ---- component ---- */
export default function PhoneInput({ value, onChange, error }) {
  const ref = useRef(null);
  const [foc, setFoc] = useState(false);

  function apply(newDigits, digitsBeforeCursor) {
    const formatted = formatPhone(newDigits);
    onChange(formatted);
    // Восстанавливаем позицию курсора
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const pos = cursorForDigits(formatted, digitsBeforeCursor);
      el.setSelectionRange(pos, pos);
    });
  }

  function handleChange(e) {
    const el = ref.current;
    const cursorPos = el.selectionStart;
    const raw = e.target.value;

    // Сколько цифр до курсора в новом (ещё не форматированном) вводе
    const digitsBeforeCursor = digitsOnly(raw.slice(0, cursorPos)).length;
    const newDigits = normalizeDigits(raw);
    apply(newDigits, digitsBeforeCursor);
  }

  function handleKeyDown(e) {
    const el = ref.current;
    if (!el) return;
    const pos = el.selectionStart;
    const val = el.value;

    if (e.key === 'Backspace' && pos === el.selectionEnd) {
      // Если курсор стоит сразу после нецифры — перешагиваем её
      // и удаляем предыдущую ЦИФРУ
      if (pos > 0 && !/\d/.test(val[pos - 1])) {
        e.preventDefault();
        // Удаляем последнюю цифру ДО курсора
        const before = val.slice(0, pos);
        const after  = val.slice(pos);
        const beforeDigits = digitsOnly(before);
        const removedDigits = beforeDigits.slice(0, -1); // убрать последнюю цифру
        const allDigits = normalizeDigits(removedDigits + digitsOnly(after));
        apply(allDigits, removedDigits.length);
      }
    }

    if (e.key === 'Delete' && pos === el.selectionEnd) {
      // Если курсор стоит перед нецифрой — удаляем следующую цифру
      if (pos < val.length && !/\d/.test(val[pos])) {
        e.preventDefault();
        const before = val.slice(0, pos);
        const after  = val.slice(pos);
        const afterDigits = digitsOnly(after);
        const removedAfter = afterDigits.slice(1); // убрать первую цифру после курсора
        const beforeDigits = digitsOnly(before);
        const allDigits = normalizeDigits(beforeDigits + removedAfter);
        apply(allDigits, beforeDigits.length);
      }
    }
  }

  function handleFocus() {
    setFoc(true);
    // Если поле пустое — сразу показываем +7
    if (!value) {
      onChange('+7 (');
      requestAnimationFrame(() => {
        if (ref.current) ref.current.setSelectionRange(4, 4);
      });
    } else {
      // Ставим курсор в конец
      requestAnimationFrame(() => {
        if (ref.current) {
          const len = ref.current.value.length;
          ref.current.setSelectionRange(len, len);
        }
      });
    }
  }

  function handleBlur() {
    setFoc(false);
    // Если ввели только +7 (без цифр абонента) — очищаем поле
    const d = normalizeDigits(value);
    if (d.length <= 1) onChange('');
  }

  // Выделение при клике на начало строки (+7) — смещаем к первому полю
  function handleClick() {
    const el = ref.current;
    if (!el) return;
    const d = normalizeDigits(value);
    if (el.selectionStart <= 2 && d.length >= 1) {
      // Поставить курсор после '+7 (' — туда, где первая цифра оператора
      const pos = value.startsWith('+7') ? 4 : 0;
      el.setSelectionRange(pos, pos);
    }
  }

  const done = isValidKzPhone(value);

  return (
    <input
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onClick={handleClick}
      placeholder="+7 (7__) ___-__-__"
      style={{
        width: '100%', height: 52, padding: '0 16px 0 42px',
        background: 'var(--surface)', color: 'var(--fg)',
        fontFamily: 'var(--font-mono)', fontSize: 15, letterSpacing: '0.04em',
        border: `1px solid ${error ? 'var(--danger)' : done ? 'var(--ok)' : foc ? 'var(--accent)' : 'var(--line-strong)'}`,
        borderRadius: 'var(--r-md)', outline: 'none',
        boxShadow: foc ? (error ? '0 0 0 3px rgba(255,77,77,0.14)' : '0 0 0 3px rgba(225,18,23,0.14)') : 'none',
        transition: 'border .18s, box-shadow .18s',
      }}
    />
  );
}
